import React, { useState, useEffect } from 'react';
import { listMagicWords, createMagicWord, updateMagicWord, deleteMagicWord } from '../services/magicWordsApi';
import './MagicWordsPage.css';

function MagicWordsPage() {
  const [magicWords, setMagicWords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [previousTokens, setPreviousTokens] = useState([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMagicWord, setSelectedMagicWord] = useState(null);

  // Form states
  const [newDescription, setNewDescription] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);

  useEffect(() => {
    fetchMagicWords();
  }, []);

  const fetchMagicWords = async (token = null) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listMagicWords(20, token);
      setMagicWords(response.magicWords);
      setNextToken(response.nextToken);
    } catch (err) {
      setError('Failed to fetch magic words: ' + err.message);
      console.error('Error fetching magic words:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateMagicWord = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await createMagicWord(newDescription);
      setNewDescription('');
      setShowCreateModal(false);
      // Refresh the list
      await fetchMagicWords();
    } catch (err) {
      setError('Failed to create magic word: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateMagicWord = async (e) => {
    e.preventDefault();
    if (!selectedMagicWord) return;

    setIsLoading(true);
    try {
      const updates = {};
      if (editDescription !== selectedMagicWord.description) {
        updates.description = editDescription;
      }
      if (editIsActive !== selectedMagicWord.isActive) {
        updates.isActive = editIsActive;
      }

      if (Object.keys(updates).length > 0) {
        await updateMagicWord(selectedMagicWord.magicWord, updates);
        // Refresh the list
        await fetchMagicWords();
      }

      setShowEditModal(false);
      setSelectedMagicWord(null);
    } catch (err) {
      setError('Failed to update magic word: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMagicWord = async () => {
    if (!selectedMagicWord) return;

    setIsLoading(true);
    try {
      await deleteMagicWord(selectedMagicWord.magicWord);
      setShowDeleteModal(false);
      setSelectedMagicWord(null);
      // Refresh the list
      await fetchMagicWords();
    } catch (err) {
      setError('Failed to delete magic word: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (magicWord) => {
    setSelectedMagicWord(magicWord);
    setEditDescription(magicWord.description || '');
    setEditIsActive(magicWord.isActive);
    setShowEditModal(true);
  };

  const openDeleteModal = (magicWord) => {
    setSelectedMagicWord(magicWord);
    setShowDeleteModal(true);
  };

  const handleNextPage = () => {
    if (nextToken && !isLoading) {
      setPreviousTokens([...previousTokens, nextToken]);
      fetchMagicWords(nextToken);
    }
  };

  const handlePreviousPage = () => {
    if (previousTokens.length > 0 && !isLoading) {
      const newPreviousTokens = [...previousTokens];
      newPreviousTokens.pop();
      setPreviousTokens(newPreviousTokens);

      const previousToken = newPreviousTokens[newPreviousTokens.length - 1] || null;
      fetchMagicWords(previousToken);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here
      alert('Magic word copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <div className="magic-words-page">
      <div className="page-header">
        <h2>Magic Words</h2>
        <button
          className="create-btn"
          onClick={() => setShowCreateModal(true)}
          disabled={isLoading}
        >
          + Create New Magic Word
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {isLoading && (
        <div className="loading-indicator">
          <p>Loading magic words...</p>
        </div>
      )}

      <div className="magic-words-table">
        <table>
          <thead>
            <tr>
              <th>Magic Word</th>
              <th>Status</th>
              <th>Description</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {magicWords.map((magicWord) => (
              <tr key={magicWord.magicWord} className={!magicWord.isActive ? 'inactive' : ''}>
                <td className="magic-word-cell">
                  <code className="magic-word-code">{magicWord.magicWord}</code>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(magicWord.magicWord)}
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </td>
                <td>
                  <span className={`status-badge ${magicWord.isActive ? 'active' : 'inactive'}`}>
                    {magicWord.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{magicWord.description || '-'}</td>
                <td>{formatDate(magicWord.createdAt)}</td>
                <td className="actions-cell">
                  <button
                    className="edit-btn"
                    onClick={() => openEditModal(magicWord)}
                    disabled={isLoading}
                  >
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => openDeleteModal(magicWord)}
                    disabled={isLoading}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {magicWords.length > 0 && (
        <div className="pagination">
          <button
            onClick={handlePreviousPage}
            disabled={previousTokens.length === 0 || isLoading}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {previousTokens.length + 1}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!nextToken || isLoading}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {magicWords.length === 0 && !isLoading && (
        <div className="empty-state">
          <p>No magic words found. Create your first magic word to get started!</p>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={() => setShowCreateModal(false)}>&times;</span>
            <h3>Create New Magic Word</h3>
            <form onSubmit={handleCreateMagicWord}>
              <div className="form-group">
                <label htmlFor="description">Description (optional):</label>
                <input
                  type="text"
                  id="description"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Enter a description for this magic word"
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={isLoading}>
                  {isLoading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMagicWord && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={() => setShowEditModal(false)}>&times;</span>
            <h3>Edit Magic Word</h3>
            <form onSubmit={handleUpdateMagicWord}>
              <div className="form-group">
                <label>Magic Word:</label>
                <code className="readonly-code">{selectedMagicWord.magicWord}</code>
              </div>
              <div className="form-group">
                <label htmlFor="editDescription">Description:</label>
                <input
                  type="text"
                  id="editDescription"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Enter a description for this magic word"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editIsActive}
                    onChange={(e) => setEditIsActive(e.target.checked)}
                  />
                  Active
                </label>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={isLoading}>
                  {isLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedMagicWord && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={() => setShowDeleteModal(false)}>&times;</span>
            <h3>Delete Magic Word</h3>
            <p>Are you sure you want to delete this magic word?</p>
            <code className="readonly-code">{selectedMagicWord.magicWord}</code>
            <p className="warning-text">This action cannot be undone!</p>
            <div className="form-actions">
              <button type="button" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="danger-btn"
                onClick={handleDeleteMagicWord}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MagicWordsPage;